import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, Trash2, Eye, X, Rocket, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const brl = (n) => `R$ ${Number(n || 0).toFixed(2).replace(".", ",")}`;
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("pt-BR");
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm font-medium text-slate-800">{value || "—"}</p>
  </div>
);

export const DoctorsAdmin = ({ token }) => {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };

  const loadDoctors = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/backoffice/doctors`, { headers: auth });
      const d = await r.json();
      if (r.ok) setDoctors(d.doctors || []);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => { loadDoctors(); }, [loadDoctors]);

  const activate = async (id, value) => {
    try {
      const r = await fetch(`${API}/api/backoffice/doctors/${id}/activate`, {
        method: "POST", headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify({ activate: value }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha");
      toast.success(value ? "Acesso liberado" : "Acesso bloqueado");
      loadDoctors();
    } catch (e) { toast.error(e.message); }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const r = await fetch(`${API}/api/backoffice/doctors/${id}`, { headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha");
      setDetail(d);
    } catch (e) { toast.error(e.message); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const doDelete = async () => {
    const id = confirmDel.id;
    try {
      const r = await fetch(`${API}/api/backoffice/doctors/${id}`, { method: "DELETE", headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha");
      toast.success("Cadastro excluído");
      setConfirmDel(null);
      setDoctors((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(e.message); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? doctors.filter((u) => `${u.clinic_name} ${u.name} ${u.email} ${u.crm}`.toLowerCase().includes(q))
    : doctors;

  return (
    <div className="space-y-5" data-testid="doctors-admin">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-bold text-slate-900">Médicos e Clínicas cadastrados</h2>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="doctors-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, e-mail ou CRM" className="w-64 max-w-full rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400">
              <th className="py-2 pr-3">Nome</th><th className="pr-3">E-mail</th><th className="pr-3">Tipo</th>
              <th className="pr-3">Plano</th><th className="pr-3">Assinatura</th><th className="pr-3">Acesso</th><th className="text-right">Ações</th>
            </tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`doctor-row-${u.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-3 font-semibold text-slate-800">
                    {u.clinic_name || u.name}{u.crm ? ` · CRM ${u.crm}${u.uf ? "-" + u.uf : ""}` : ""}
                    {u.founder_member && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700"><Rocket className="h-2.5 w-2.5" />Fundador</span>}
                  </td>
                  <td className="pr-3 text-slate-500">{u.email}</td>
                  <td className="pr-3 text-slate-500">{u.role === "admin" ? "Clínica" : "Médico"}</td>
                  <td className="pr-3 text-slate-500">{u.plan || "—"}</td>
                  <td className="pr-3 text-slate-500">{u.subscription_status || "—"}</td>
                  <td className="pr-3">{u.activation_status === "activated"
                    ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />Liberado</span>
                    : <span className="inline-flex items-center gap-1 text-amber-600"><Clock className="h-3.5 w-3.5" />Bloqueado</span>}</td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`doctor-detail-${u.id}`} onClick={() => openDetail(u.id)} title="Detalhes"
                        className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-100"><Eye className="h-4 w-4" /></button>
                      {u.activation_status === "activated"
                        ? <button data-testid={`doctor-block-${u.id}`} onClick={() => activate(u.id, false)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Bloquear</button>
                        : <button data-testid={`doctor-activate-${u.id}`} onClick={() => activate(u.id, true)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">Autorizar acesso</button>}
                      <button data-testid={`doctor-delete-${u.id}`} onClick={() => setConfirmDel({ id: u.id, name: u.clinic_name || u.name })} title="Excluir"
                        className="rounded-lg border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">Nenhum cadastro encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="doctor-detail-modal" onClick={() => setDetail(null)}>
          <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {detail.loading || detailLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-extrabold text-slate-900">{detail.doctor.clinic_name || detail.doctor.name}</h3>
                    <p className="text-sm text-slate-500">{detail.doctor.role === "admin" ? "Clínica" : "Médico"} · cadastro em {fmtDate(detail.doctor.created_at)}</p>
                  </div>
                  <button data-testid="doctor-detail-close" onClick={() => setDetail(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                <div className="grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3">
                  <Field label="Nome" value={detail.doctor.name} />
                  <Field label="E-mail" value={detail.doctor.email} />
                  <Field label="Telefone" value={detail.doctor.phone} />
                  <Field label="CRM / UF" value={detail.doctor.crm ? `${detail.doctor.crm}${detail.doctor.uf ? " - " + detail.doctor.uf : ""}` : ""} />
                  <Field label="Especialidade" value={detail.doctor.specialty} />
                  <Field label="Plano" value={detail.doctor.plan} />
                  <Field label="Modalidade" value={detail.doctor.modality} />
                  <Field label="Valor presencial" value={detail.doctor.price_presencial ? brl(detail.doctor.price_presencial) : ""} />
                  <Field label="Valor online" value={detail.doctor.price_online ? brl(detail.doctor.price_online) : ""} />
                  <Field label="Assinatura" value={detail.doctor.subscription_status} />
                  <Field label="Acesso" value={detail.doctor.activation_status === "activated" ? "Liberado" : "Bloqueado"} />
                  <Field label="Fundador" value={detail.doctor.founder_member ? `Sim · ${brl(detail.doctor.founder_price)}/mês` : "Não"} />
                  <div className="col-span-2 sm:col-span-3"><Field label="Endereço" value={detail.doctor.address} /></div>
                </div>

                <h4 className="mb-2 mt-5 font-bold text-slate-900">Histórico de mensalidades</h4>
                {detail.mensalidades && detail.mensalidades.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 text-left text-xs text-slate-400">
                        <th className="px-3 py-2">Vencimento</th><th className="px-3">Valor</th><th className="px-3">Forma</th><th className="px-3">Pagamento</th><th className="px-3">Status</th><th className="px-3"></th>
                      </tr></thead>
                      <tbody>
                        {detail.mensalidades.map((m, i) => (
                          <tr key={i} data-testid={`mensalidade-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-2">{fmtDate(m.due_date)}</td>
                            <td className="px-3">{brl(m.value)}</td>
                            <td className="px-3 text-slate-500">{m.billing_type || "—"}</td>
                            <td className="px-3 text-slate-500">{fmtDate(m.payment_date)}</td>
                            <td className="px-3">{m.paid
                              ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />Paga</span>
                              : <span className="inline-flex items-center gap-1 font-semibold text-amber-600"><Clock className="h-3.5 w-3.5" />Pendente</span>}</td>
                            <td className="px-3">{m.invoice_url && <a href={m.invoice_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:underline">Fatura</a>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400" data-testid="mensalidades-empty">
                    {detail.doctor.asaas_subscription_id ? "Nenhuma mensalidade registrada ainda." : "Sem assinatura recorrente no Asaas (ainda não contratou um plano pago)."}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="doctor-delete-confirm" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-slate-900">Excluir cadastro</h3>
            <p className="mt-2 text-sm text-slate-600">Tem certeza que deseja excluir <b>{confirmDel.name}</b>? Esta ação não pode ser desfeita.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button data-testid="doctor-delete-confirm-btn" onClick={doDelete} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
